import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { MAX_RECORD } from 'constants/Global';
import { fetchBrandsByGroup, fetchBrandsByDealer, fetchBrand } from 'modules/brands/actions';

import { getAllBrands, getBrandsApiError, getBrandsIsFetching, getBrandsByGroupId, getBrandsByDealerId } from 'modules/brands/selectors';
import type { RootState } from 'reducers/index';
import { hasAccessToDealer } from 'selectors/dealerSelectors';

const defaultParameters = {
  sort: 'name,asc',
  fields: 'id,name'
};

type UseBrandsParams = {
  groupId?: string;
  dealerId?: string;
}

const useBrands = (params: UseBrandsParams = {}) => {
  const { groupId, dealerId } = params;

  const dispatch = useDispatch();
  const isFetching = useSelector(getBrandsIsFetching);
  const apiError = useSelector(getBrandsApiError);
  const allBrands = useSelector(getAllBrands);

  const selector = groupId ? getBrandsByGroupId : getBrandsByDealerId;
  const callId = groupId ?? dealerId;
  let brands = useSelector((state: RootState) => selector(state, callId!));
  // @ts-expect-error selectors are not correctly typed
  const canAccessToDealer = useSelector(state => hasAccessToDealer(state, dealerId));
  if (!groupId && !dealerId) {
    brands = allBrands;
  }

  React.useEffect(() => {
    const payload = {
      ...defaultParameters,
    };

    if (Object.keys(brands ?? {}).length === 0) {
      if (groupId) {
        dispatch(fetchBrandsByGroup(groupId, { ...payload, size: MAX_RECORD }));
      } else if (dealerId && canAccessToDealer) {
        dispatch(fetchBrandsByDealer(dealerId, { ...payload, size: MAX_RECORD }));
      }
    }
  // adding brands would create a loop
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, dealerId, groupId, canAccessToDealer]);

  const fetchBrandById = (brandId: string) => {
    dispatch(fetchBrand(brandId));
  };

  return {
    allBrands,
    brands,
    isFetching,
    fetchBrandById,
    apiError,
    firstBrand: Object.values(brands ?? {})[0] ?? {},
  };
};

export default useBrands;
